/**
 * Product page behaviour.
 *
 * <rz-product>: when an option changes, fetch this section for the chosen option values
 * (Section Rendering API, ?option_values=) and swap every [data-product-part]. The gallery
 * is not re-rendered; it just jumps to the variant's media.
 * <rz-gallery>: slider, thumbnails, counter, zoom (hover / click / lightbox), media grouping.
 */

if (!customElements.get('rz-product')) {
  customElements.define(
    'rz-product',
    class extends HTMLElement {
      connectedCallback() {
        if (this.dataset.productId && this.hasAttribute('data-record-recent')) this.recordRecent();
        this.syncQuantityBreaks();
        this.addEventListener('change', (event) => {
          if (event.target.closest('[data-variant-picker]')) this.onOptionChange(event.target);
        });
        this.addEventListener('click', (event) => {
          const step = event.target.closest('[data-quantity]');
          if (step) this.stepQuantity(step);
          // "Add all" (add-ons, layout "Bought together"): tick every add-on, then add as the main button does.
          if (event.target.closest('[data-add-ons-all]')) {
            this.querySelectorAll('[data-add-on-item]').forEach((input) => (input.checked = true));
            const form = this.querySelector('[data-product-form]');
            form?.requestSubmit(form.querySelector('[name="add"]'));
          }
        });
        this.addEventListener('submit', (event) => {
          // 'no-js' lets the form post normally (some apps rely on the full page load).
          if (event.target.matches('[data-product-form]') && window.theme?.afterAdd !== 'no-js') this.addToCart(event);
        });
        // Free shipping progress depends on the cart total: refresh after cart requests made by anyone,
        // apps included (Chrome reports add.js and update.js; the theme's own change/clear come as rz:cart:* events),
        // and when the page comes back from the back/forward cache.
        if (this.querySelector('[data-free-shipping]')) {
          const cartChange = /\/cart\/(add|change|update|clear)/;
          this.cartObserver = new PerformanceObserver((list) => {
            if (!list.getEntries().some((entry) => cartChange.test(entry.name))) return;
            clearTimeout(this.freeShippingTimer);
            this.freeShippingTimer = setTimeout(() => this.refreshFreeShipping(), 400);
          });
          this.cartObserver.observe({ type: 'resource' });
          // A full resource timing buffer (250 entries by default, product pages use most of it) stops new entries.
          performance.addEventListener?.('resourcetimingbufferfull', () =>
            performance.setResourceTimingBufferSize(performance.getEntriesByType('resource').length + 500)
          );
          // Theme events as well, in case the browser has no resource timing.
          document.addEventListener('rz:cart:add', () => this.refreshFreeShipping());
          document.addEventListener('rz:cart:change', () => this.refreshFreeShipping());
          this.onPageShow = (event) => event.persisted && this.refreshFreeShipping();
          window.addEventListener('pageshow', this.onPageShow);
        }
      }

      disconnectedCallback() {
        this.cartObserver?.disconnect();
        if (this.onPageShow) window.removeEventListener('pageshow', this.onPageShow);
      }

      async refreshFreeShipping() {
        const html = await fetch(`${this.dataset.url}?section_id=${this.dataset.sectionId}`, { cache: 'no-store' }).then((r) => r.text());
        const doc = new DOMParser().parseFromString(html, 'text/html');
        doc.querySelectorAll('[data-free-shipping]').forEach((fresh) => {
          this.querySelector(`[data-product-part="${fresh.dataset.productPart}"]`)?.replaceWith(fresh);
        });
      }

      // Newest first, max 20, for the "Recently viewed" section.
      recordRecent() {
        try {
          const id = Number(this.dataset.productId);
          const list = JSON.parse(localStorage.getItem('rz-recently-viewed') || '[]').filter((x) => x !== id);
          localStorage.setItem('rz-recently-viewed', JSON.stringify([id, ...list].slice(0, 20)));
        } catch (e) {}
      }

      get gallery() {
        return this.querySelector('rz-gallery');
      }

      selectedOptionValueIds() {
        return [...this.querySelectorAll('[data-variant-picker]')].flatMap((picker) => [
          ...[...picker.querySelectorAll('input[type="radio"]:checked')].map((i) => i.value),
          ...[...picker.querySelectorAll('select')].map((s) => s.value).filter(Boolean),
        ]);
      }

      async onOptionChange(input) {
        // Combined listings: some values live on another product page.
        const option = input.tagName === 'SELECT' ? input.selectedOptions[0] : input;
        const targetUrl = option.dataset.productUrl;
        if (targetUrl && targetUrl !== this.dataset.url) {
          location.href = targetUrl;
          return;
        }

        // Nothing preselected (section setting): until every option has a value, the button names what is missing.
        const missing = [...this.querySelectorAll('[data-variant-picker] :is(fieldset, select)')].filter((group) =>
          group.tagName === 'SELECT' ? !group.value : !group.querySelector('input:checked')
        );
        if (missing.length) {
          const names = missing.map((group) =>
            (group.tagName === 'SELECT' ? group.labels?.[0] : group.querySelector('legend'))?.firstChild?.textContent.trim().toLowerCase()
          );
          const button = this.querySelector('[data-product-form] [name="add"]');
          const label = button?.querySelector('span');
          if (label && button.dataset.selectText) label.textContent = button.dataset.selectText.replace('[option]', names.join(', '));
          return;
        }

        const ids = this.selectedOptionValueIds().join(',');
        const url = `${this.dataset.url}?option_values=${ids}&section_id=${this.dataset.sectionId}`;
        this.setAttribute('aria-busy', 'true');
        const focusedId = document.activeElement?.id;
        const request = (this.variantRequest = (this.variantRequest || 0) + 1);
        // What the shopper typed (quantity, e-mail…) survives the re-render; option inputs come fresh.
        const typed = new Map(
          [...this.querySelectorAll('[data-product-part] :is(input, textarea)[id]')]
            .filter((i) => !['radio', 'checkbox', 'hidden'].includes(i.type) && i.value !== i.defaultValue)
            .map((i) => [i.id, i.value])
        );
        const tier = this.querySelector('[data-tier-pricing] input:checked')?.value;
        const plan = this.querySelector('[data-subscription] input:checked')?.value;
        // Add-ons re-render with the variant (their total follows it); the ticks and chosen options stay.
        const addOns = [...this.querySelectorAll('[data-add-on]')].map((el) => ({
          checked: el.querySelector('[data-add-on-item]')?.checked,
          variant: el.querySelector('[data-add-on-variant]')?.value,
        }));

        try {
          const html = await fetch(url).then((r) => r.text());
          // A quicker click on another option already started a newer request; drop this answer.
          if (request !== this.variantRequest) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          doc.querySelectorAll('[data-product-part]').forEach((fresh) => {
            this.querySelector(`[data-product-part="${fresh.dataset.productPart}"]`)?.replaceWith(fresh);
          });
          typed.forEach((value, id) => {
            const input = this.querySelector(`#${CSS.escape(id)}`);
            if (!input) return;
            input.value = value;
            // The new variant can have other quantity rules.
            if (input.type === 'number' && !input.checkValidity()) input.value = input.min || input.defaultValue;
          });
          // The chosen quantity tier stays chosen, unless the new variant doesn't have enough stock for it.
          const tierInput = tier && this.querySelector(`[data-tier-pricing] input[value="${tier}"]:not(:disabled)`);
          if (tierInput) tierInput.checked = true;
          // The chosen subscription plan (or one-time) stays chosen when the new variant offers it.
          const planInput = plan !== undefined && this.querySelector(`[data-subscription] input[value="${plan}"]`);
          if (planInput) planInput.checked = true;
          this.querySelectorAll('[data-add-on]').forEach((el, i) => {
            const kept = addOns[i];
            if (!kept) return;
            el.querySelector('[data-add-on-item]').checked = kept.checked;
            const select = el.querySelector('[data-add-on-variant]');
            if (select && kept.variant) select.value = kept.variant;
          });
          this.syncQuantityBreaks();
          if (focusedId) document.getElementById(focusedId)?.focus({ preventScroll: true });
          this.announcePrice();

          const variantId = this.querySelector('[data-product-form] input[name="id"]')?.value;
          const freshGallery = doc.querySelector('rz-gallery');
          const activeMediaId = freshGallery?.querySelector('[data-active]')?.dataset.mediaId;
          this.gallery?.setGroup(freshGallery?.dataset.group);
          if (activeMediaId) this.gallery?.goToMedia(activeMediaId, true);

          // Only the main product page owns the URL (not quick add or featured product).
          if (variantId && this.hasAttribute('data-update-url')) {
            const params = new URLSearchParams(location.search);
            params.set('variant', variantId);
            history.replaceState({}, '', `${location.pathname}?${params}`);
          }
          this.dispatchEvent(new CustomEvent('rz:variant:change', { bubbles: true, detail: { variantId } }));
        } finally {
          if (request === this.variantRequest) this.removeAttribute('aria-busy');
        }
      }

      // The price and the buy button change silently when they are swapped in; read them out once.
      announcePrice() {
        const price = this.querySelector('[data-product-part] .price__current')?.textContent.replace(/\s+/g, ' ').trim();
        const button = this.querySelector('[data-product-form] [name="add"]');
        const state = button?.disabled ? button.textContent.replace(/\s+/g, ' ').trim() : '';
        if (!price && !state) return;
        let status = this.querySelector('[data-variant-status]');
        if (!status) {
          status = document.createElement('p');
          status.className = 'visually-hidden';
          status.setAttribute('aria-live', 'polite');
          status.dataset.variantStatus = '';
          this.append(status);
        }
        status.textContent = [price, state].filter(Boolean).join('. ');
      }

      // Quantity break cards send the quantity; the quantity selector (hidden by CSS) must not send a second one.
      syncQuantityBreaks() {
        const tiers = !!this.querySelector('[data-tier-pricing]');
        this.querySelectorAll('.buy-buttons input.quantity__input').forEach((input) => (input.disabled = tiers));
      }

      stepQuantity(button) {
        const input = button.closest('.quantity')?.querySelector('input');
        if (!input) return;
        const step = Number(input.step) || 1;
        const min = Number(input.min) || 1;
        const max = input.max ? Number(input.max) : Infinity;
        const next = Number(input.value || min) + step * Number(button.dataset.quantity);
        input.value = Math.min(max, Math.max(min, next));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // Ticked add-ons (blocks/_product-add-ons) join the same request, so it is all or nothing.
      // Without any, the form goes as it is (apps that read the form data keep working).
      addRequest(formData) {
        // "One-time purchase" of the subscription block sends an empty plan; leave it out.
        if (formData.get('selling_plan') === '') formData.delete('selling_plan');
        const extras = [...this.querySelectorAll('[data-add-on-item]:checked')].map((input) => ({
          id: Number(input.closest('[data-add-on]').querySelector('[data-add-on-variant]')?.value || input.value),
          quantity: 1,
        }));
        // The chosen quantity tier comes with a gift: cart.js takes it out again below that quantity.
        const tierGift = this.querySelector('[data-tier-pricing] input:checked[data-gift]');
        if (tierGift) {
          extras.push({ id: Number(tierGift.dataset.gift), quantity: 1, properties: { _rz_gift: 'tier', _rz_gift_for: String(formData.get('id')), _rz_gift_min: tierGift.dataset.giftMin } });
        }
        if (!extras.length) return { headers: { Accept: 'application/json' }, body: formData };
        const main = { id: Number(formData.get('id')), quantity: Number(formData.get('quantity') || 1) };
        const properties = {};
        formData.forEach((value, key) => {
          const name = key.match(/^properties\[(.+)\]$/)?.[1];
          if (name) properties[name] = value;
        });
        if (Object.keys(properties).length) main.properties = properties;
        if (formData.get('selling_plan')) main.selling_plan = formData.get('selling_plan');
        return {
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [main, ...extras] }),
        };
      }

      async addToCart(event) {
        event.preventDefault();
        const form = event.target;
        const submit = event.submitter || form.querySelector('[type="submit"]');
        const error = form.querySelector('.product-form__error');
        submit.setAttribute('aria-busy', 'true');
        error.hidden = true;

        try {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
            method: 'POST',
            ...this.addRequest(new FormData(form)),
          });
          const data = await response.json();
          if (!response.ok) {
            error.textContent = data.description || data.message;
            error.hidden = false;
            return;
          }
          // "Buy now" (shown instead of dynamic checkout when offers are on the page) goes straight to checkout.
          if (event.submitter?.hasAttribute('data-buy-now')) {
            location.href = event.submitter.value;
            return;
          }
          // Added once; a second click on the button adds only the product.
          this.querySelectorAll('[data-add-on-item]:checked').forEach((input) => (input.checked = false));
          // cart.js reacts (open the drawer, go to the cart page or just update the count).
          if (customElements.get('rz-cart')) {
            document.dispatchEvent(new CustomEvent('rz:cart:add', { detail: { item: data, source: form } }));
          } else {
            location.href = `${window.Shopify?.routes?.root || '/'}cart`;
          }
        } catch (e) {
          // A plain form post would drop the ticked add-ons; say so instead.
          if (this.querySelector('[data-add-on-item]:checked')) {
            error.textContent = window.theme?.strings?.cartError || 'Error';
            error.hidden = false;
          } else {
            form.submit();
          }
        } finally {
          submit.removeAttribute('aria-busy');
        }
      }
    }
  );
}

if (!customElements.get('rz-gallery')) {
  customElements.define(
    'rz-gallery',
    class extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-track]');
        this.prev = this.querySelector('[data-prev]');
        this.next = this.querySelector('[data-next]');
        this.counter = this.querySelector('[data-current]');
        this.total = this.querySelector('[data-total]');

        this.prev?.addEventListener('click', () => this.step(-1));
        this.next?.addEventListener('click', () => this.step(1));
        this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });
        this.querySelectorAll('.gallery__thumb').forEach((thumb) =>
          thumb.addEventListener('click', () => this.goToMedia(thumb.closest('li').dataset.mediaId))
        );
        this.setupZoom();

        this.syncThumbs();
        const active = this.querySelector('.gallery__item[data-active]');
        // On load a stacked gallery only marks the active image: scrolling it into view would move the whole page.
        if (active && this.isStacked) this.setActive(active);
        else if (active) requestAnimationFrame(() => this.goToMedia(active.dataset.mediaId, false, 'instant'));
        else this.onScroll();
      }

      get items() {
        return [...this.querySelectorAll('.gallery__item:not([hidden])')];
      }

      get isStacked() {
        return this.classList.contains('gallery--stacked') && matchMedia('(min-width: 990px)').matches;
      }

      step(direction) {
        const index = this.currentIndex();
        const target = this.items[Math.min(this.items.length - 1, Math.max(0, index + direction))];
        if (target) this.scrollToItem(target);
      }

      currentIndex() {
        const left = this.track.scrollLeft;
        const width = this.track.clientWidth || 1;
        return Math.round(Math.abs(left) / width);
      }

      scrollToItem(item, behavior = 'smooth') {
        if (this.isStacked) {
          item.scrollIntoView({ behavior, block: 'center' });
          return;
        }
        this.track.scrollTo({ left: item.offsetLeft - this.track.offsetLeft, behavior });
      }

      goToMedia(mediaId, fromVariantChange = false, behavior = 'smooth') {
        const item = this.querySelector(`.gallery__item[data-media-id="${mediaId}"]`);
        if (!item || item.hidden) return;
        const scrollSetting = this.closest('[data-scroll-to-media]')?.dataset.scrollToMedia;
        if (this.isStacked && (scrollSetting === 'never' || (!fromVariantChange && scrollSetting === 'change'))) return;
        this.scrollToItem(item, behavior);
        this.setActive(item);
      }

      onScroll() {
        if (this.isStacked) return;
        const item = this.items[this.currentIndex()];
        if (item) this.setActive(item);
      }

      setActive(item) {
        const items = this.items;
        const index = items.indexOf(item);
        if (this.counter) this.counter.textContent = index + 1;
        if (this.total) this.total.textContent = items.length;
        if (this.prev) this.prev.disabled = index <= 0;
        if (this.next) this.next.disabled = index >= items.length - 1;
        this.querySelectorAll('.gallery__thumb').forEach((thumb) =>
          thumb.setAttribute('aria-current', String(thumb.closest('li').dataset.mediaId === item.dataset.mediaId))
        );
        // Pause any playing video that scrolled out of view.
        this.querySelectorAll('.gallery__item video').forEach((v) => v.closest('.gallery__item') !== item && v.pause());
      }

      setGroup(group) {
        if (!this.hasAttribute('data-grouping') || group === undefined) return;
        this.dataset.group = group;
        this.querySelectorAll('.gallery__item[data-group]').forEach((item) => (item.hidden = item.dataset.group !== group));
        this.syncThumbs();
        // The lightbox list is built from the visible images on first open; rebuild it for the new group.
        this.querySelector('[data-lightbox-list]')?.replaceChildren();
      }

      syncThumbs() {
        this.querySelectorAll('.gallery__thumbs li').forEach((thumb) => {
          const item = this.querySelector(`.gallery__item[data-media-id="${thumb.dataset.mediaId}"]`);
          thumb.hidden = !!item?.hidden;
        });
      }

      /* Zoom ---------------------------------------------------------------- */

      setupZoom() {
        const mode = this.dataset.zoom;
        if (mode === 'none') return;
        const desktop = matchMedia('(min-width: 990px) and (hover: hover)');
        const level = this.dataset.zoomLevel;

        this.addEventListener('click', (event) => {
          const target = event.target.closest('.gallery__zoom-target');
          if (!target) return;
          if (!desktop.matches) {
            if (this.dataset.lightboxMobile === 'true') this.openLightbox(target);
            return;
          }
          if (mode === 'lightbox') this.openLightbox(target);
          else if (mode === 'click') this.toggleZoom(target, event, level);
        });

        if (mode === 'hover') {
          this.addEventListener('mousemove', (event) => {
            const target = event.target.closest('.gallery__zoom-target');
            if (!target || !desktop.matches) return;
            this.zoomAt(target, event, level);
          });
          this.addEventListener('mouseout', (event) => {
            const target = event.target.closest('.gallery__zoom-target');
            if (target && !target.contains(event.relatedTarget)) target.classList.remove('is-zoomed');
          });
        }
        if (mode === 'click') {
          this.addEventListener('mousemove', (event) => {
            const target = event.target.closest('.gallery__zoom-target.is-zoomed');
            if (target) this.zoomAt(target, event, level);
          });
        }
      }

      zoomAt(target, event, level) {
        const img = target.querySelector('img');
        if (!img) return;
        // Swap in the large file once, the first time this image is zoomed.
        if (!img.dataset.zoomLoaded) {
          img.srcset = '';
          img.src = target.dataset.zoomSrc;
          img.dataset.zoomLoaded = 'true';
        }
        const rect = target.getBoundingClientRect();
        const scale = level === 'original' ? Math.max(1.5, Number(target.dataset.width) / rect.width) : Number(level);
        target.style.setProperty('--zoom-scale', scale);
        target.style.setProperty('--zoom-x', `${((event.clientX - rect.left) / rect.width) * 100}%`);
        target.style.setProperty('--zoom-y', `${((event.clientY - rect.top) / rect.height) * 100}%`);
        target.classList.add('is-zoomed');
      }

      toggleZoom(target, event, level) {
        if (target.classList.contains('is-zoomed')) target.classList.remove('is-zoomed');
        else this.zoomAt(target, event, level);
      }

      openLightbox(target) {
        const dialog = this.querySelector('.gallery__lightbox');
        const list = dialog?.querySelector('[data-lightbox-list]');
        if (!dialog || !list) return;
        if (!list.children.length) {
          this.querySelectorAll('.gallery__item:not([hidden]) .gallery__zoom-target').forEach((t) => {
            const li = document.createElement('li');
            const img = document.createElement('img');
            img.src = t.dataset.zoomSrc;
            img.alt = t.querySelector('img')?.alt || '';
            img.loading = 'lazy';
            img.dataset.mediaId = t.closest('.gallery__item').dataset.mediaId;
            li.append(img);
            list.append(li);
          });
        }
        dialog.showModal();
        const mediaId = target.closest('.gallery__item').dataset.mediaId;
        list.querySelector(`img[data-media-id="${mediaId}"]`)?.scrollIntoView({ block: 'start' });
      }
    }
  );
}

/**
 * <rz-sticky-atc>: shows once the main buy buttons have scrolled out of view,
 * hides again near the page bottom so it never covers the footer.
 * Detailed layout: the name is cut after the last whole word that fits in its two lines.
 */
if (!customElements.get('rz-sticky-atc')) {
  customElements.define(
    'rz-sticky-atc',
    class extends HTMLElement {
      connectedCallback() {
        const product = this.closest('rz-product');
        this.target = () => product?.querySelector('[data-product-form]');
        this.update = this.update.bind(this);
        addEventListener('scroll', this.update, { passive: true });
        this.update();
        if (this.classList.contains('sticky-atc--detailed')) {
          this.fitTitle = this.fitTitle.bind(this);
          addEventListener('resize', this.fitTitle);
          this.fitTitle();
        }
      }

      disconnectedCallback() {
        removeEventListener('scroll', this.update);
        if (this.fitTitle) removeEventListener('resize', this.fitTitle);
      }

      // line-clamp alone may cut the last word in half; drop whole words until the rest fits, then add "…".
      fitTitle() {
        const title = this.querySelector('[data-sticky-title]');
        if (!title) return;
        const full = (title.dataset.full ??= title.textContent.trim());
        title.textContent = full;
        if (title.scrollHeight <= title.clientHeight + 1) return;
        const words = full.split(/\s+/);
        while (words.length > 1) {
          words.pop();
          title.textContent = `${words.join(' ').replace(/[\s,.;:–-]+$/, '')}…`;
          if (title.scrollHeight <= title.clientHeight + 1) break;
        }
      }

      update() {
        const form = this.target();
        if (!form) return;
        const passed = form.getBoundingClientRect().bottom < 0;
        const nearEnd = innerHeight + scrollY > document.documentElement.scrollHeight - 300;
        this.hidden = !passed || nearEnd;
      }
    }
  );
}
